import { afterNextRender, Component, computed, ElementRef, inject, Injector, signal, viewChild } from '@angular/core';
import { DocumentItemTypeEnum } from '@domain';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaButtonComponent, RaDialogConfirmationService, RaIconComponent, RaModalComponent } from 'ra-ui';
import { finalize } from 'rxjs';
import {
    CHART_HEIGHT_OPTIONS,
    CHART_KIND_OPTIONS,
    CHART_LEGEND_OPTIONS,
    CHART_MAX_SERIES,
    CHART_PALETTE_OPTIONS,
    CHART_UNIT_OPTIONS,
    exampleChartValue,
    gridFromMatrix,
    parseChartValue,
    parseDelimitedText,
} from '../../../../../chart-options';
import {
    CHART_LIMITS,
    CHART_PALETTES,
    chartNumericCount,
    ChartKind,
    ChartStyle,
    ChartValue,
    DEFAULT_CHART_STYLE,
    initialChartValue,
    isLineKind,
    isPieKind,
    parseChartNumber,
} from '../../../../../chart-render/chart-render';
import { ChartRenderService } from '../../../../../chart-render/chart-render.service';
import { parseTableValue } from '../../../../../table-options';
import { EditDataService } from '../../../services/_edit-data.service';
import { ChartWizardService, ChartWizardStep } from '../../../services/chart-wizard.service';
import { EditItemsService } from '../../../services/edit-items.service';
import { EditService } from '../../../services/edit-service';

interface DocTableOption {
    id: string;
    heading: string;
    sectionName: string;
    rowsCount: number;
    matrix: string[][];
}

const STEPS: { key: ChartWizardStep; labelKey: string }[] = [
    { key: 'type', labelKey: 'document.edit.chart.steps.type' },
    { key: 'data', labelKey: 'document.edit.chart.steps.data' },
    { key: 'look', labelKey: 'document.edit.chart.steps.look' },
];

/**
 * Assistente do GRÁFICO (item CHART), em três etapas: o tipo, os dados e a aparência.
 *
 * Para criar, abre pelo menu "Inserir campo" com dados de exemplo (o cliente vê um gráfico
 * de verdade desde o começo) e só cria o item no "Inserir gráfico". Para editar, abre pelo
 * card na etapa pedida, já com o gráfico gravado. A prévia ao lado é o SVG que vai para o
 * PDF, desenhado na hora pelo mesmo código da API.
 */
@Component({
    selector: 'modal-chart',
    templateUrl: 'modal-chart.component.html',
    styleUrl: 'modal-chart.component.scss',
    imports: [RaModalComponent, RaButtonComponent, RaIconComponent, TranslatePipe],
})
export class ModalChartComponent {
    private _wizard = inject(ChartWizardService);
    private _editItems = inject(EditItemsService);
    private _editData = inject(EditDataService);
    private _editService = inject(EditService);
    private _translate = inject(TranslateService);
    private _host = inject<ElementRef<HTMLElement>>(ElementRef);
    private _injector = inject(Injector);
    private _confirm = inject(RaDialogConfirmationService);
    private _modal = viewChild.required(RaModalComponent);
    public chartRender = inject(ChartRenderService);

    public readonly steps = STEPS;
    public readonly kinds = CHART_KIND_OPTIONS;
    public readonly palettes = CHART_PALETTE_OPTIONS;
    public readonly legends = CHART_LEGEND_OPTIONS;
    public readonly units = CHART_UNIT_OPTIONS;
    public readonly heights = CHART_HEIGHT_OPTIONS;
    public readonly limits = CHART_LIMITS;
    public readonly maxSeries = CHART_MAX_SERIES;

    private readonly _state = this._wizard.state();
    public readonly mode = this._state?.mode ?? 'create';

    // * [Estado do gráfico em edição]
    public step = signal<ChartWizardStep>(this._state?.step ?? 'type');
    public kind = signal<ChartKind | null>(null);
    public title = signal('');
    public columns = signal<string[]>(['', '']);
    public rows = signal<string[][]>([]);
    public style = signal<ChartStyle>({ ...DEFAULT_CHART_STYLE });
    /** Ainda são os dados de exemplo: o aviso fica na tela até o cliente mexer. */
    public isExample = signal(false);

    // * [Interface]
    public notice = signal<{ tone: 'ok' | 'error'; text: string } | null>(null);
    public pasteOpen = signal(false);
    public pasteText = signal('');
    public tablesOpen = signal(false);
    public saving = signal(false);

    public value = computed<ChartValue>(() => ({
        kind: this.kind() ?? 'column',
        title: this.title(),
        columns: this.columns(),
        rows: this.rows(),
        style: this.style(),
    }));

    /** Como o gráfico estava ao abrir (sem o tipo, no gráfico novo): base do "descartar?". */
    private _initial: ChartValue = initialChartValue();
    public isDirty = computed(() => this._snapshot() !== this._snapshot(this._initial));

    public numericCount = computed(() => chartNumericCount(this.value()));
    /** SVG da prévia, desenhado a cada mudança (o ECharts leva poucos milissegundos). */
    public preview = computed(() => this.chartRender.trust(this.chartRender.render(this.value())));
    public isPie = computed(() => isPieKind(this.kind() ?? 'column'));
    public isLine = computed(() => isLineKind(this.kind() ?? 'column'));
    /** Na pizza os rótulos são percentuais: o formato dos números não muda nada nela. */
    public showUnit = computed(() => this.kind() !== 'pie');
    public seriesCount = computed(() => this.columns().length - 1);
    public seriesColors = computed(() => CHART_PALETTES[this.style().palette] ?? CHART_PALETTES.default);
    public kindOption = computed(() => this.kinds.find(k => k.key === (this.kind() ?? 'column')) ?? this.kinds[0]);
    public stepIndex = computed(() => STEPS.findIndex(s => s.key === this.step()));
    public gridTemplate = computed(
        () => `minmax(7.5rem, 1.25fr) repeat(${this.seriesCount()}, minmax(6.5rem, 1fr)) 2.5rem`
    );

    public canContinue = computed(() => {
        switch (this.step()) {
            case 'type':
                return !!this.kind();
            case 'data':
                return this.numericCount() > 0;
            default:
                return !this.saving();
        }
    });

    /** As tabelas do documento, para virar gráfico sem digitar de novo. */
    public docTables = computed<DocTableOption[]>(() => {
        const options: DocTableOption[] = [];
        for (const session of this._editData.document()?.sessions ?? []) {
            for (const item of session.items ?? []) {
                if (item.type !== DocumentItemTypeEnum.TABLE) continue;
                const table = parseTableValue(item.value);
                const filled = table.rows.filter(r => r.some(c => c.trim() !== ''));
                if (filled.length === 0) continue;
                options.push({
                    id: item._id,
                    heading: table.columns.filter(c => c.trim() !== '').join(' · ') || '—',
                    sectionName: session.name ?? '',
                    rowsCount: filled.length,
                    matrix: [table.columns, ...filled],
                });
            }
        }
        return options;
    });

    constructor() {
        void this.chartRender.load();

        // Foco inicial de cada etapa. Roda antes do foco automático do ra-modal (que pegaria
        // o primeiro input, o de arquivo escondido em "Importar CSV").
        afterNextRender(() => this._focusStep());

        const state = this._state;
        if (state?.mode === 'edit') {
            const field = this._editData.findFieldAnywhere(state.fieldId)?.field;
            const value = parseChartValue(field?.value) ?? initialChartValue();
            this.kind.set(value.kind);
            this.title.set(value.title);
            this.columns.set(value.columns);
            this.rows.set(value.rows.length > 0 ? value.rows : [value.columns.map(() => '')]);
            this.style.set(value.style);
        } else {
            const example = exampleChartValue('column', key => this._translate.instant(key));
            this.title.set(example.title);
            this.columns.set(example.columns);
            this.rows.set(example.rows);
            this.isExample.set(true);
        }
        this._initial = this.value();
    }

    /**
     * Troca o tipo. Enquanto a tabela ainda for o exemplo, ele acompanha o tipo (pizza e rosca
     * vêm com uma série; os outros, com duas): uma coluna "fora da pizza" no exemplo só
     * confunde. O título fica como está, e a base do "descartar?" acompanha a troca para que
     * ela sozinha não conte como mexida.
     */
    private _setKind(kind: ChartKind): void {
        if (this.kind() === kind) return;
        this.kind.set(kind);
        if (!this.isExample()) return;
        const { columns, rows } = exampleChartValue(kind, key => this._translate.instant(key));
        this.columns.set(columns);
        this.rows.set(rows);
        this._initial = { ...this._initial, columns, rows };
    }

    /**
     * O que o cliente preencheu. No gráfico novo o tipo fica de fora: só escolher um tipo e
     * fechar não pergunta nada.
     */
    private _snapshot(value: ChartValue = this.value()): string {
        const { kind, ...rest } = value;
        return JSON.stringify(this.mode === 'edit' ? { kind, ...rest } : rest);
    }

    // #region * [Etapas]

    public canGo(step: ChartWizardStep): boolean {
        if (this.saving()) return false;
        if (step === 'type') return true;
        if (!this.kind()) return false;
        return step === 'data' || this.numericCount() > 0;
    }

    public goTo(step: ChartWizardStep): void {
        if (!this.canGo(step)) return;
        this.tablesOpen.set(false);
        this.step.set(step);
        afterNextRender(() => this._focusStep(), { injector: this._injector });
    }

    /** Tipo: o escolhido (ou o primeiro). Dados: a primeira célula. Aparência: o título. */
    private _focusStep(): void {
        const host = this._host.nativeElement;
        const target =
            this.step() === 'type'
                ? host.querySelector<HTMLElement>('.type.current') ?? host.querySelector<HTMLElement>('.type')
                : this.step() === 'data'
                  ? host.querySelector<HTMLElement>('[data-cell="0:0"]')
                  : host.querySelector<HTMLElement>('.field input');
        target?.focus({ preventScroll: true });
    }

    public pickKind(kind: ChartKind): void {
        this._setKind(kind);
    }

    /** Dois cliques num tipo: escolhe e já vai para os dados. */
    public pickKindAndGo(kind: ChartKind): void {
        this._setKind(kind);
        this.goTo('data');
    }

    public back(): void {
        const index = this.stepIndex();
        if (index > 0) this.goTo(STEPS[index - 1].key);
    }

    public next(): void {
        if (!this.canContinue()) return;
        if (this.step() === 'type') return this.goTo('data');
        if (this.step() === 'data') return this.goTo('look');
        this.finish();
    }

    /**
     * O ra-modal avisa depois de esconder o painel. Sem nada preenchido, fecha; com dados,
     * pergunta antes de jogar fora, e "Continuar editando" traz o painel de volta.
     */
    public close(): void {
        if (this.saving()) return;
        if (!this.isDirty()) {
            this._wizard.close();
            return;
        }

        const key = 'document.edit.chart.discard';
        let confirmed = false;
        this._confirm
            .confirm({
                title: this._translate.instant(`${key}.${this.mode === 'edit' ? 'titleEdit' : 'titleCreate'}`),
                message: this._translate.instant(`${key}.message`),
                confirmText: this._translate.instant(`${key}.confirm`),
                cancelText: this._translate.instant(`${key}.cancel`),
                type: 'warning',
            })
            .subscribe({
                next: control => {
                    confirmed = true;
                    control.close();
                    this._wizard.close();
                },
                complete: () => {
                    if (!confirmed) this._modal().cancelClose();
                },
            });
    }

    /** Cria o item (gráfico novo) ou grava o que mudou (gráfico existente). */
    private finish(): void {
        const value: ChartValue = {
            ...this.value(),
            rows: this.rows().filter(r => r.some(c => c.trim() !== '')),
        };
        const state = this._state;

        if (state?.mode === 'edit') {
            const svg = this.chartRender.render(value);
            this._editItems.updateChart(state.fieldId, svg ? { ...value, svg } : value);
            this._wizard.close();
            return;
        }

        this.saving.set(true);
        this._editService.loading.set(true);
        this._editItems
            .addChart(value, state?.aboveFieldId, state?.belowFieldId)
            .pipe(
                finalize(() => {
                    this.saving.set(false);
                    this._editService.loading.set(false);
                })
            )
            .subscribe({
                next: response => {
                    this._wizard.close();
                    const id = response?.fieldsId?.[0];
                    if (id) this._revealWhenRendered(id);
                },
                error: error => console.error(error),
            });
    }

    /** O card novo chega pelo eco do servidor: espera ele existir e rola até ele. */
    private _revealWhenRendered(fieldId: string, attempt = 0): void {
        const card = document.querySelector<HTMLElement>(`[data-field-id="${fieldId}"]`);
        if (card) {
            card.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        if (attempt < 10) setTimeout(() => this._revealWhenRendered(fieldId, attempt + 1), 150);
    }

    // #endregion

    // #region * [Dados]

    /** "1 linha" / "6 linhas": o ngx-translate não tem plural, a chave escolhe. */
    public countLabel(kind: 'rows' | 'series', n: number): string {
        return this._translate.instant(`document.edit.chart.count.${kind}.${n === 1 ? 'one' : 'other'}`, { n });
    }

    public isBadNumber(text: string): boolean {
        return text.trim() !== '' && parseChartNumber(text) === null;
    }

    public setHeader(column: number, text: string): void {
        this.columns.update(c => c.map((v, i) => (i === column ? text : v)));
        this._touched();
    }

    public setCell(row: number, column: number, text: string): void {
        this.rows.update(r => r.map((cells, ri) => (ri === row ? cells.map((v, ci) => (ci === column ? text : v)) : cells)));
        this._touched();
    }

    public addRow(focus = true): void {
        if (this.rows().length >= CHART_LIMITS.MAX_ROWS) return;
        this.rows.update(r => [...r, this.columns().map(() => '')]);
        this._touched();
        if (focus) {
            const row = this.rows().length - 1;
            setTimeout(() => this.focusCell(row, 0));
        }
    }

    public removeRow(row: number): void {
        if (this.rows().length <= 1) return;
        this.rows.update(r => r.filter((_, i) => i !== row));
        this._touched();
    }

    public addSeries(): void {
        if (this.columns().length >= CHART_LIMITS.MAX_COLUMNS) return;
        this.columns.update(c => [...c, '']);
        this.rows.update(r => r.map(cells => [...cells, '']));
        this._touched();
        const column = this.columns().length - 1;
        setTimeout(() => this.focusCell(-1, column));
    }

    public removeSeries(column: number): void {
        if (this.columns().length <= CHART_LIMITS.MIN_COLUMNS) return;
        this.columns.update(c => c.filter((_, i) => i !== column));
        this.rows.update(r => r.map(cells => cells.filter((_, i) => i !== column)));
        this._touched();
    }

    /** Começa do zero: uma série, três linhas vazias. */
    public clearData(): void {
        this.columns.set(['', '']);
        this.rows.set([
            ['', ''],
            ['', ''],
            ['', ''],
        ]);
        this.title.set('');
        this.isExample.set(false);
        this.notice.set(null);
        setTimeout(() => this.focusCell(0, 0));
    }

    /** Enter desce para a mesma coluna da linha de baixo (criando a linha no fim). */
    public onCellKeydown(event: KeyboardEvent, row: number, column: number): void {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        const next = row + 1;
        if (next >= this.rows().length) this.addRow(false);
        setTimeout(() => this.focusCell(next, column));
    }

    /**
     * Colou mais de uma célula (veio de planilha): espalha a partir de onde colou. Com os
     * dados de exemplo na tela, ou colando no canto de cima, troca a tabela inteira.
     */
    public onCellPaste(event: ClipboardEvent, row: number, column: number): void {
        const text = event.clipboardData?.getData('text/plain') ?? '';
        if (!/[\t\n]/.test(text.replace(/\s+$/, ''))) return;
        event.preventDefault();

        const matrix = parseDelimitedText(text);
        if (this.isExample() || (row <= 0 && column === 0)) {
            this._replaceAll(matrix, this._translate.instant('document.edit.chart.data.fromPaste'));
            return;
        }
        this._spreadAt(row, column, matrix);
    }

    public togglePaste(): void {
        this.pasteOpen.update(open => !open);
        this.tablesOpen.set(false);
        if (this.pasteOpen()) setTimeout(() => this._host.nativeElement.querySelector<HTMLElement>('.paste-box textarea')?.focus());
    }

    public toggleTables(): void {
        this.tablesOpen.update(open => !open);
        this.pasteOpen.set(false);
    }

    /** Colou na caixa "Colar do Excel": já usa, sem precisar de outro clique. */
    public onPasteBox(event: ClipboardEvent): void {
        const text = event.clipboardData?.getData('text/plain') ?? '';
        if (!text.trim()) return;
        event.preventDefault();
        this._replaceAll(parseDelimitedText(text), this._translate.instant('document.edit.chart.data.fromPaste'));
    }

    public applyPaste(): void {
        this._replaceAll(parseDelimitedText(this.pasteText()), this._translate.instant('document.edit.chart.data.fromPaste'));
    }

    public onFile(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        if (!file) return;

        if (/\.xlsx?$/i.test(file.name)) {
            this.notice.set({ tone: 'error', text: this._translate.instant('document.edit.chart.data.xlsxHint') });
            return;
        }

        file.text()
            .then(text => this._replaceAll(parseDelimitedText(text), file.name))
            .catch(() => this.notice.set({ tone: 'error', text: this._translate.instant('document.edit.chart.data.fileError') }));
    }

    public useTable(table: DocTableOption): void {
        this._replaceAll(
            table.matrix,
            this._translate.instant('document.edit.chart.data.fromTable', { name: table.heading })
        );
    }

    public focusCell(row: number, column: number): void {
        this._host.nativeElement.querySelector<HTMLInputElement>(`[data-cell="${row}:${column}"]`)?.focus();
    }

    private _touched(): void {
        this.isExample.set(false);
        this.notice.set(null);
    }

    private _replaceAll(matrix: string[][], source: string): void {
        const grid = gridFromMatrix(matrix, text => parseChartNumber(text) !== null);
        if (!grid) {
            this.notice.set({ tone: 'error', text: this._translate.instant('document.edit.chart.data.nothingFound') });
            return;
        }

        this.columns.set(grid.columns);
        this.rows.set(grid.rows);
        this.isExample.set(false);
        if (this.mode === 'create' && this.title() === this._translate.instant('document.edit.chart.example.title')) {
            this.title.set('');
        }
        this.pasteOpen.set(false);
        this.pasteText.set('');
        this.tablesOpen.set(false);
        this.notice.set({
            tone: 'ok',
            text: this._translate.instant('document.edit.chart.data.imported', {
                source,
                rows: this.countLabel('rows', grid.rows.length),
                series: this.countLabel('series', grid.columns.length - 1),
            }),
        });
    }

    private _spreadAt(row: number, column: number, matrix: string[][]): void {
        let columns = [...this.columns()];
        let rows = this.rows().map(r => [...r]);
        let start = row;
        let lines = matrix;

        if (row === -1) {
            matrix[0].forEach((text, j) => (columns[column + j] = text));
            lines = matrix.slice(1);
            start = 0;
        }
        lines.forEach((line, i) => {
            while (rows.length <= start + i) rows.push([]);
            line.forEach((text, j) => (rows[start + i][column + j] = text));
        });

        const width = Math.min(
            CHART_LIMITS.MAX_COLUMNS,
            Math.max(columns.length, ...rows.map(r => r.length))
        );
        columns = Array.from({ length: width }, (_, i) => columns[i] ?? '');
        rows = rows.slice(0, CHART_LIMITS.MAX_ROWS).map(r => Array.from({ length: width }, (_, i) => r[i] ?? ''));

        this.columns.set(columns);
        this.rows.set(rows);
        this._touched();
    }

    // #endregion

    // #region * [Aparência]

    public setStyle<K extends keyof ChartStyle>(key: K, value: ChartStyle[K]): void {
        this.style.update(s => ({ ...s, [key]: value }));
    }

    // #endregion
}
